import React, { useState, useRef, useEffect } from 'react';
import { useServerStore } from '../../store/serverStore';
import { useMessageStore } from '../../store/messageStore';
import { useFriendStore } from '../../store/friendStore';
import { useVoiceStore } from '../../store/voiceStore';
import { Hash, Home, Volume2, Search, Users, X, Phone } from 'lucide-react';
import SearchModal from './SearchModal';

interface HeaderProps {
  isMemberPanelOpen?: boolean;
  onToggleMembers?: () => void;
}

const Header: React.FC<HeaderProps> = ({ isMemberPanelOpen = false, onToggleMembers }) => {
  const { currentServer, currentChannel } = useServerStore();
  const { currentDmUser } = useMessageStore();
  const { startDmCall, dmCallStatus } = useVoiceStore();
  const { friends } = useFriendStore();
  
  const dmPartner = currentDmUser ? friends.find(f => f.friend_id === currentDmUser)?.friend : null;
  
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input when shown
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchModal(true);
    }
  };

  return (
    <header className="h-12 bg-bg-secondary flex items-center justify-between px-4 z-10 border-t border-b border-white/10">
      <div className="flex items-center min-w-0">
        {currentServer && (
          <>
            <div className="flex items-center">
              {!currentChannel ? (
                <Home className="h-4 w-4 text-text-muted mr-2" />
              ) : currentChannel.type === 'text' ? (
                <Hash className="h-4 w-4 text-text-muted mr-2" />
              ) : currentChannel.type === 'voice' ? (
                <Volume2 className="h-4 w-4 text-text-muted mr-2" />
              ) : (
                <div className="h-4 w-4 bg-accent-color/80 rounded text-white flex items-center justify-center mr-2 text-[8px] font-bold">C</div>
              )}
              <h1 className="text-sm font-bold text-text-primary">{currentChannel?.name || 'Home'}</h1>
            </div>
            
            {currentChannel?.description && (
              <>
                <div className="w-px h-4 bg-border-color mx-3" />
                <span className="text-text-muted text-xs truncate max-w-md">{currentChannel.description}</span>
              </>
            )}
          </>
        )}
        
        {currentDmUser && !currentServer && (
          <div className="flex items-center gap-2.5">
            <div className="relative flex-shrink-0">
              <div className="w-7 h-7 rounded-full overflow-hidden bg-highlight">
                {dmPartner?.avatar_url ? (
                  <img src={dmPartner.avatar_url} alt={dmPartner.display_name || dmPartner.username} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-accent-color/80 text-white flex items-center justify-center text-xs font-bold">
                    {(dmPartner?.display_name || dmPartner?.username || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-bg-secondary ${dmPartner?.status === 'online' ? 'bg-success' : 'bg-text-muted/40'}`} />
            </div>
            <span className="text-sm font-bold text-text-primary">{dmPartner?.display_name || dmPartner?.username || 'Message privé'}</span>
          </div>
        )}
        
        {!currentServer && !currentDmUser && (
          <div className="flex items-center">
            <span className="text-sm font-bold text-text-primary">Amis</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-1">
        {currentDmUser && !currentServer && (
          <button
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
              dmCallStatus !== 'idle'
                ? 'text-success cursor-not-allowed'
                : 'text-text-muted hover:text-text-primary hover:bg-highlight/50'
            }`}
            onClick={() => dmCallStatus === 'idle' && startDmCall(currentDmUser!)}
            disabled={dmCallStatus !== 'idle'}
            title={dmCallStatus !== 'idle' ? 'Appel en cours' : 'Appeler'}
          >
            <Phone className="h-4 w-4" />
          </button>
        )}
        
        {currentServer && (
          <>
          {showSearch ? (
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="bg-bg-primary/50 border border-border-color rounded-lg py-1.5 pl-8 pr-8 w-56 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-color/50 transition-colors"
                onBlur={() => {
                  if (!searchQuery) setShowSearch(false);
                }}
              />
              <Search className="h-3.5 w-3.5 text-text-muted absolute left-2.5 top-1/2 transform -translate-y-1/2" />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </form>
          ) : (
            <button
              className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-all"
              onClick={() => setShowSearch(true)}
              title="Search"
            >
              <Search className="h-4 w-4" />
            </button>
          )}
          <button 
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
              isMemberPanelOpen ? 'text-text-primary bg-highlight/70' : 'text-text-muted hover:text-text-primary hover:bg-highlight/50'
            }`}
            onClick={onToggleMembers}
            title="Members"
          >
            <Users className="h-4 w-4" />
          </button>
          </>
        )}
        
        {!currentServer && (showSearch ? (
          <form onSubmit={handleSearchSubmit} className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="bg-bg-primary/50 border border-border-color rounded-lg py-1.5 pl-8 pr-8 w-56 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-color/50 transition-colors"
              onBlur={() => {
                if (!searchQuery) setShowSearch(false);
              }}
            />
            <Search className="h-3.5 w-3.5 text-text-muted absolute left-2.5 top-1/2 transform -translate-y-1/2" />
            {searchQuery && (
              <button 
                type="button"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>
        ) : (
          <button 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-all"
            onClick={() => setShowSearch(true)}
            title="Search"
          >
            <Search className="h-4 w-4" />
          </button>
        ))}
      </div>
      
      {showSearchModal && (
        <SearchModal 
          query={searchQuery} 
          onClose={() => {
            setShowSearchModal(false);
            setSearchQuery('');
            setShowSearch(false);
          }} 
        />
      )}
      
    </header>
  );
};

export default Header;
